import React, { useMemo } from 'react';
import {
  UserPlus,
  Play,
  Pause,
  RotateCcw,
  CheckCircle2,
  XCircle,
  ArrowRight,
  AlertTriangle,
  MessageSquare,
  SkipForward,
  Clock,
  Paperclip,
  Download,
} from 'lucide-react';

interface LogEntry {
  id: string;
  action: string;
  performed_by: string;
  performed_by_name?: string;
  performed_by_role?: string;
  justification?: string;
  metadata?: Record<string, any>;
  created_at?: string;
  timestamp?: string;
}

interface MinhaMesaTimelineProps {
  logs: LogEntry[];
  isLoading?: boolean;
  /** Map of userId → display name for resolving performer */
  userNames?: Record<string, string>;
  currentUserId?: string;
}

const ACTION_META: Record<string, { icon: typeof Play; label: string; color: string }> = {
  claim: { icon: UserPlus, label: 'Assumida', color: 'text-blue-600 dark:text-blue-400' },
  status_change: { icon: Play, label: 'Status alterado', color: 'text-slate-600 dark:text-slate-400' },
  reassign: { icon: ArrowRight, label: 'Transferida', color: 'text-violet-600 dark:text-violet-400' },
  escalate: { icon: AlertTriangle, label: 'Escalada', color: 'text-amber-600 dark:text-amber-400' },
  return: { icon: RotateCcw, label: 'Devolvida', color: 'text-orange-600 dark:text-orange-400' },
  note: { icon: MessageSquare, label: 'Observação', color: 'text-sky-600 dark:text-sky-400' },
  skip: { icon: SkipForward, label: 'Pulada', color: 'text-slate-500' },
  complete: { icon: CheckCircle2, label: 'Concluída', color: 'text-emerald-600 dark:text-emerald-400' },
  cancel: { icon: XCircle, label: 'Cancelada', color: 'text-red-600 dark:text-red-400' },
  adjust_sla: { icon: Clock, label: 'SLA ajustado', color: 'text-indigo-600 dark:text-indigo-400' },
  register_problem: { icon: AlertTriangle, label: 'Problema registrado', color: 'text-red-600 dark:text-red-400' },
  attachment: { icon: Paperclip, label: 'Anexo', color: 'text-indigo-600 dark:text-indigo-400' },
};

function resolveStatusLabel(log: LogEntry): string {
  const meta = log.metadata ?? {};
  if (log.action === 'status_change') {
    const from = meta.from as string | undefined;
    const to = meta.to as string | undefined;
    if (to === 'in_progress' && from === 'pending') return 'Iniciada';
    if (to === 'in_progress' && from === 'paused') return 'Retomada';
    if (to === 'paused') return 'Pausada';
    if (to === 'completed') return 'Concluída';
    if (to === 'failed') return meta.result === 'rejected' ? 'Devolvida' : 'Falha';
    return `${from ?? '?'} → ${to ?? '?'}`;
  }
  return ACTION_META[log.action]?.label ?? log.action;
}

function resolveIcon(log: LogEntry) {
  if (log.action === 'status_change') {
    const to = (log.metadata?.to as string) ?? '';
    if (to === 'in_progress') return Play;
    if (to === 'paused') return Pause;
    if (to === 'completed') return CheckCircle2;
    if (to === 'failed') return XCircle;
  }
  return ACTION_META[log.action]?.icon ?? Clock;
}

function resolveColor(log: LogEntry): string {
  if (log.action === 'status_change') {
    const to = (log.metadata?.to as string) ?? '';
    if (to === 'in_progress') return 'text-emerald-600 dark:text-emerald-400';
    if (to === 'paused') return 'text-amber-600 dark:text-amber-400';
    if (to === 'completed') return 'text-emerald-600 dark:text-emerald-400';
    if (to === 'failed') return 'text-red-600 dark:text-red-400';
  }
  return ACTION_META[log.action]?.color ?? 'text-slate-500';
}

function formatTimeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export const MinhaMesaTimeline: React.FC<MinhaMesaTimelineProps> = ({ logs, isLoading, userNames, currentUserId }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <span className="text-[10px] enterprise-text-muted">Carregando histórico...</span>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex items-center justify-center py-6">
        <span className="text-[10px] enterprise-text-muted">Nenhum evento registrado</span>
      </div>
    );
  }

  const resolveUserLabel = (log: LogEntry): string => {
    if (currentUserId && log.performed_by === currentUserId) return 'Você';
    if (log.performed_by_name) return log.performed_by_name;
    return userNames?.[log.performed_by] ?? log.performed_by.slice(0, 8);
  };

  return (
    <div className="relative space-y-0 pl-4">
      {/* Vertical line */}
      <div className="absolute bottom-0 left-[7px] top-0 w-px bg-[var(--border-default)]" />

      {logs.map((log) => {
        const Icon = resolveIcon(log);
        const color = resolveColor(log);
        const label = resolveStatusLabel(log);
        const ts = log.timestamp ?? log.created_at ?? '';
        const userName = resolveUserLabel(log);
        const rawJustification = (log.justification ?? '').replace(/\.+$/, '').trim();
        const attachments = (log.metadata?.attachments ?? []) as Array<{ name: string; url: string; size?: string }>;
        const problemType = log.metadata?.problemType as string | undefined;
        const impact = log.metadata?.impact as string | undefined;

        return (
          <div key={log.id} className="relative flex gap-2.5 pb-3">
            {/* Dot */}
            <div className={`relative z-10 -ml-4 mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[var(--bg-surface)] ${color}`}>
              <Icon size={9} />
            </div>
            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[10px] font-semibold enterprise-text-primary">{label}</span>
                <span className="font-mono text-[9px] enterprise-text-muted">{userName}</span>
                {ts && <span className="font-mono text-[8px] enterprise-text-muted">{formatTimeAgo(ts)}</span>}
              </div>
              {/* Note text */}
              {rawJustification && log.action === 'note' && (
                <p className="mt-0.5 text-[10px] leading-relaxed enterprise-text-secondary">{rawJustification}</p>
              )}
              {/* Problem details */}
              {log.action === 'register_problem' && (
                <div className="mt-0.5 space-y-0.5">
                  {problemType && <span className="mr-2 inline-block rounded bg-red-100 px-1.5 py-0.5 font-mono text-[8px] font-semibold uppercase text-red-700 dark:bg-red-900/30 dark:text-red-400">{problemType}</span>}
                  {impact && <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[8px] font-semibold uppercase text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">{impact}</span>}
                  {rawJustification && <p className="text-[10px] leading-relaxed enterprise-text-secondary">{rawJustification}</p>}
                </div>
              )}
              {/* Other justifications */}
              {rawJustification && log.action !== 'note' && log.action !== 'register_problem' && rawJustification.length > 3 && (
                <p className="mt-0.5 text-[9px] italic enterprise-text-muted">{rawJustification}</p>
              )}
              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {attachments.map((att, i) => (
                    <a
                      key={i}
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[9px] text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      <Download size={8} />
                      <span className="truncate">{att.name}</span>
                      {att.size && <span className="enterprise-text-muted">({att.size})</span>}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
